import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X, Tv, Wifi } from 'lucide-react';
import { valorPorPagamento, labelPlano } from './valorPagamento';

// Soma das internets adicionais (além da principal)
export const totalInternetsAdicionais = (d) =>
  (d.internets_adicionais || []).reduce((s, i) => s + (Number(i.valor) || 0) * (Number(i.qtd) || 1), 0);

// Lista de planos para o resumo lateral / detalhes
export const planosExtrasResumo = (d) =>
  (d.internets_adicionais || []).filter((i) => i.nome).map((i) => ({ nome: `${i.nome} (Internet adicional)`, valor: Number(i.valor) || 0, qtd: Number(i.qtd) || 1 }));

const fmt = (v) => `R$ ${(Number(v) || 0).toFixed(2)}`;

// TV → Pontos adicionais (reaproveita plano_pontosadicionais_*: id/nome = tipo, valor = unitário, qtd = quantidade)
export const TvPontosAdicionais = ({ data, onChange, planosDisponiveis, categoria, formaPagamento }) => {
  const tipos = planosDisponiveis.filter((p) => p.tipo === 'PONTOS ADICIONAIS' && p.categoria === categoria && p.ativo !== false);
  const temTipos = tipos.length > 0;
  const possui = !!data.tv_possui_ponto_adicional;
  const qtd = Number(data.plano_pontosadicionais_qtd) || 1;
  const unit = Number(data.plano_pontosadicionais_valor) || 0;

  const toggle = (checked) => onChange(checked
    ? { tv_possui_ponto_adicional: true, plano_pontosadicionais_qtd: qtd }
    : { tv_possui_ponto_adicional: false, plano_pontosadicionais_id: '', plano_pontosadicionais_nome: '', plano_pontosadicionais_valor: 0, plano_pontosadicionais_qtd: 1 });

  const escolherTipo = (id) => {
    if (id === 'nenhum') return onChange({ plano_pontosadicionais_id: '', plano_pontosadicionais_nome: '', plano_pontosadicionais_valor: 0 });
    const p = tipos.find((t) => t.id === id);
    onChange({ plano_pontosadicionais_id: id, plano_pontosadicionais_nome: p?.nome_plano || '', plano_pontosadicionais_valor: p ? valorPorPagamento(p, formaPagamento) : 0 });
  };

  return (
    <div className="mt-2 p-3 rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-950/10 space-y-2" data-testid="tv-pontos-adicionais">
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" className="h-4 w-4 accent-blue-600" checked={possui} onChange={(e) => toggle(e.target.checked)} data-testid="tv-possui-ponto-adicional" />
        <span className="text-xs font-semibold text-blue-800 dark:text-blue-300 flex items-center gap-1"><Tv className="h-3.5 w-3.5" /> Possui ponto adicional?</span>
      </label>
      {possui && (
        <div className="grid grid-cols-2 gap-2 items-end">
          <div>
            <Label className="text-[11px] text-gray-500">Quantidade</Label>
            <Select value={String(qtd)} onValueChange={(v) => onChange({ plano_pontosadicionais_qtd: parseInt(v, 10) })}>
              <SelectTrigger className="h-9" data-testid="tv-pontos-qtd"><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-gray-500">Valor unitário {temTipos && <span className="text-gray-400">(do cadastro)</span>}</Label>
            <Input type="number" step="0.01" min={0} value={unit} readOnly={temTipos} disabled={temTipos} onChange={(e) => onChange({ plano_pontosadicionais_valor: parseFloat(e.target.value) || 0 })} className={`h-9 ${temTipos ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : ''}`} data-testid="tv-pontos-valor-unitario" />
          </div>
          <div className="col-span-2 min-w-0">
            <Label className="text-[11px] text-gray-500">Tipo do ponto adicional {temTipos && <span className="text-red-500">*</span>}</Label>
            <Select value={data.plano_pontosadicionais_id || 'nenhum'} onValueChange={escolherTipo}>
              <SelectTrigger className="h-9 w-full" data-testid="tv-pontos-tipo"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nenhum">{temTipos ? 'Selecione o tipo...' : 'Nenhum tipo cadastrado em Valores'}</SelectItem>
                {tipos.map((t) => <SelectItem key={t.id} value={t.id}>{labelPlano(t, formaPagamento)}</SelectItem>)}
              </SelectContent>
            </Select>
            {!temTipos && <p className="text-[11px] text-amber-600 mt-1">Nenhum tipo de ponto cadastrado em Valores para esta categoria — informe o valor manualmente.</p>}
          </div>
          <p className="col-span-2 text-xs font-semibold text-blue-700 dark:text-blue-300" data-testid="tv-pontos-total">
            Valor total dos pontos adicionais: {fmt(unit)} x {qtd} = {fmt(unit * qtd)}
          </p>
        </div>
      )}
    </div>
  );
};

// Internet → várias internets na mesma venda ("+ Adicionar Internet")
export const InternetsAdicionais = ({ data, onChange, planosDisponiveis, categoria, formaPagamento }) => {
  const planos = planosDisponiveis.filter((p) => p.tipo === 'INTERNET' && p.categoria === categoria);
  const lista = data.internets_adicionais || [];
  const setItem = (idx, patch) => onChange({ internets_adicionais: lista.map((i, k) => (k === idx ? { ...i, ...patch } : i)) });
  const escolher = (idx, id) => {
    const p = planos.find((x) => x.id === id);
    setItem(idx, { plano_id: id, nome: p?.nome_plano || '', valor: p ? valorPorPagamento(p, formaPagamento) : 0 });
  };

  return (
    <div className="mt-2 space-y-2" data-testid="internets-adicionais">
      {lista.map((item, idx) => (
        <div key={idx} className="flex gap-2 items-center" data-testid={`internet-adicional-${idx}`}>
          <Wifi className="h-4 w-4 text-gray-400 shrink-0" />
          <Select value={item.plano_id || 'nenhum'} onValueChange={(v) => escolher(idx, v)}>
            <SelectTrigger className="flex-1 h-9" data-testid={`internet-adicional-select-${idx}`}><SelectValue placeholder="Selecione a internet..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="nenhum">Selecione...</SelectItem>
              {planos.map((p) => <SelectItem key={p.id} value={p.id}>{labelPlano(p, formaPagamento)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="number" min={1} value={item.qtd || 1} onChange={(e) => setItem(idx, { qtd: Math.max(1, parseInt(e.target.value, 10) || 1) })} className="w-16 h-9 text-center" data-testid={`internet-adicional-qtd-${idx}`} />
          <span className="text-[11px] text-red-600 font-medium whitespace-nowrap w-20 text-right">{fmt((Number(item.valor) || 0) * (Number(item.qtd) || 1))}</span>
          <button type="button" onClick={() => onChange({ internets_adicionais: lista.filter((_, k) => k !== idx) })} className="text-red-500 px-1" title="Remover" data-testid={`internet-adicional-remove-${idx}`}><X className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange({ internets_adicionais: [...lista, { plano_id: '', nome: '', valor: 0, qtd: 1 }] })} className="flex items-center gap-1 px-2 py-1 rounded border border-dashed border-red-400 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 text-[11px] font-medium" data-testid="btn-adicionar-internet">
        <Plus className="h-3 w-3" /> Adicionar Internet
      </button>
    </div>
  );
};

// Há tipos de ponto adicional cadastrados (ativos) para a categoria?
export const temTiposPontoAdicional = (planosDisponiveis, categoria) =>
  (planosDisponiveis || []).some((p) => p.tipo === 'PONTOS ADICIONAIS' && p.categoria === categoria && p.ativo !== false);

// Validação: internet adicional sem plano escolhido / pontos adicionais sem tipo
export const validarPlanosExtras = (d, temTipos = false) => {
  const inv = (d.internets_adicionais || []).findIndex((i) => !i.plano_id || i.plano_id === 'nenhum');
  if (inv >= 0) return `Internet adicional ${inv + 1}: selecione o plano ou remova a linha`;
  if (d.tv_possui_ponto_adicional) {
    if (temTipos && !d.plano_pontosadicionais_id) return 'Pontos adicionais: selecione o tipo do ponto (HD, 4K...)';
    if (!temTipos && !(Number(d.plano_pontosadicionais_valor) > 0)) return 'Pontos adicionais: informe o valor unitário';
  }
  return null;
};
